import { useEffect, useMemo, useState } from 'react';

import { useLocation } from 'react-router';

import type { ExperimentVariantUnion } from 'src/app/context/ExperimentsContext';
import { useOrderCount } from 'src/app/hooks/data/order';
import { usePrimaryPatient } from 'src/app/hooks/data/patient';
import { loggedInTransferAppInterstitialPrompt, medications } from 'src/app/router/paths';
import { getHasPrimaryPatientEverTransferred } from 'src/app/state/selectors';
import { useSelector } from 'src/app/state/store';
import { SESSION_STORAGE_KEYS } from 'src/app/storage/sessionStorageKeys';
import { isHydra } from 'src/app/utils/browser';

import { useAccount } from '../data/account';
import { useUser } from '../data/user';
import useAuth from '../useAuth';
import useExperiments from '../useExperiments';

const getSessionStorageExperimentSeen = (): boolean =>
  !!window.sessionStorage.getItem(SESSION_STORAGE_KEYS.hasSeenPOTSInterstitial);

const setSessionStorageExperimentSeen = () =>
  window.sessionStorage.setItem(SESSION_STORAGE_KEYS.hasSeenPOTSInterstitial, 'true');

const usePOTSExperimentActivate = () => {
  const { data: primaryPatient, isLoading: isPrimaryPatientLoading } = usePrimaryPatient();
  const { data: orders_placed, isLoading: isOrderCountLoading } = useOrderCount();
  const numberOfPrimaryPatientOtherMeds = primaryPatient?.otherMedications?.length ?? 0;
  const hasPrimaryPatientEverTransferred = useSelector(getHasPrimaryPatientEverTransferred);
  const { loggedInUser, isLoggedIn } = useAuth();
  const { data: user } = useUser(loggedInUser ? loggedInUser.uuid : '', isLoggedIn);
  const { data: account, isLoading: isAccountLoading } = useAccount(user ? parseInt(user.accountId) : -1, {
    enabled: true,
    refetchOnWindowFocus: false,
  });
  const [isLoadingActivateExperiment, setIsLoadingActivateExperiment] = useState<boolean>(true);
  const [variant, setVariant] = useState<ExperimentVariantUnion<'mcyp_pots_interstitials_only'> | null>(null);

  const attributesToReportToAmplitude = useMemo(
    () => ({
      orders_placed,
      has_ever_transferred: hasPrimaryPatientEverTransferred,
      take_other_meds: numberOfPrimaryPatientOtherMeds > 0,
    }),
    [orders_placed, hasPrimaryPatientEverTransferred, numberOfPrimaryPatientOtherMeds]
  );
  const { fetchVariant } = useExperiments();

  useEffect(() => {
    setIsLoadingActivateExperiment(true);
    // Wait for user, primary patient and order count to load before evaluating the experiment
    if (!!user && !!account && !isAccountLoading && !isPrimaryPatientLoading && !isOrderCountLoading) {
      const evaluatePOTSExperiment = async () => {
        const variant = await fetchVariant({
          experimentKey: 'mcyp_pots_interstitials_only',
          userId: user.accountId,
          audienceAttributes: {
            /*
             * Audience eligibility is determined in Amplitude
             * As of 06/27/2024, the user is eligible if:
             *   - Has never transferred a medication
             *   - Has placed at least one order but fewer than 5
             *   - Takes other meds
             *   - Acquisition source is not null
             */
            ...attributesToReportToAmplitude,
            acquisition_source: account.signupSource,
          },
        });
        setIsLoadingActivateExperiment(false);
        setVariant(variant);
      };
      evaluatePOTSExperiment();
    }
  }, [
    user,
    attributesToReportToAmplitude,
    fetchVariant,
    isOrderCountLoading,
    isPrimaryPatientLoading,
    account,
    isAccountLoading,
  ]);

  useEffect(() => {
    // If we're not loading, then we know the attributes are all defined.
    if (!isLoadingActivateExperiment) {
      window.analytics.identify(attributesToReportToAmplitude);
    }
  }, [isLoadingActivateExperiment, attributesToReportToAmplitude]);

  return {
    variant: isHydra() ? undefined : variant,
    isLoadingActivateExperiment,
  };
};

const checkIfInterstitialVariant = (variant?: ExperimentVariantUnion<'mcyp_pots_interstitials_only'>) =>
  variant === 'interstitial_bring_all_meds' || variant === 'interstitial_complete_checklist';

export const usePOTSExperimentInterstitial = () => {
  const { pathname } = useLocation();

  const { variant, isLoadingActivateExperiment } = usePOTSExperimentActivate();

  const isInterstitialVariant = useMemo(() => checkIfInterstitialVariant(variant), [variant]);

  const isHomePage = pathname === medications.route;
  const isInterstitialPage = pathname === loggedInTransferAppInterstitialPrompt.route;

  const hasSeenPOTSInterstitialThisSession = getSessionStorageExperimentSeen();

  const shouldShowBasedOnAmplitude: boolean = isInterstitialVariant && !isLoadingActivateExperiment;

  /**
   * If we're on the correct route, the user hasn't seen the experiment this session, and they should
   * be in the experiment (according to Amplitude or local storage), then we should show the interstitial.
   */
  const shouldShow: boolean = isHomePage && !hasSeenPOTSInterstitialThisSession && shouldShowBasedOnAmplitude;

  // Hook to ensure relevant session storage is set when the user sees the interstitial.
  useEffect(() => {
    const hasMarkedSeenInSessionStorage = getSessionStorageExperimentSeen();

    if (isInterstitialPage && !hasMarkedSeenInSessionStorage) {
      setSessionStorageExperimentSeen();
    }
  }, [isInterstitialPage]);

  return { shouldShow, isLoading: isLoadingActivateExperiment, variant };
};
